// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import {
  ApplicationInsights,
  IApplicationInsights,
  IDependencyTelemetry,
  IEventTelemetry,
  IExceptionTelemetry
} from '@microsoft/applicationinsights-web';
import { v4 as uuid } from 'uuid';

export type BotServerAppInsightsEvent =
  | 'BotSession_NewSession'
  | 'BotSession_TriggerNewBotSession'
  | 'BotSession_MediaClient_Create'
  | 'BotSession_MediaSessionAgent_Create'
  | 'BotSession_Connect'
  | 'BotSession_JoinSession'
  | 'BotSession_ConfigureRequest'
  | 'BotSession_StreamUpdated'
  | 'BotSession_Mute'
  | 'BotSession_Unmute'
  | 'BotSession_End'
  | 'BotSession_ServerCrash'
  | 'BotSession_UnknownException';

/**
 * BotServerAppInsights is a wrapper around Application Insights to track events.
 * It injects the operationId into the properties of each event.
 * It also tracks the session state and ensures that events are only tracked when a session is open.
 * It is used to track events related to the speak to bot feature where correlation id must be used.
 */
export class BotServerAppInsights {
  public operationId: string | undefined;
  private appInsights: IApplicationInsights | undefined;
  private sessionOpen = false;

  constructor(connectionString: string | undefined) {
    if (!connectionString) {
      console.warn('No connection string found. Bot Server App Insights will not be initialized.');
      return;
    }
    const appInsightsInstance = new ApplicationInsights({
      config: {
        disableCookiesUsage: true,
        connectionString
      }
    });
    appInsightsInstance.loadAppInsights();
    this.appInsights = appInsightsInstance;
  }

  public newSession(): void {
    this.operationId = uuid();
    this.sessionOpen = true;
    this.trackEvent({
      name: 'BotSession_NewSession'
    });
  }

  public finishSession(): void {
    this.sessionOpen = false;
  }

  public dispose(): void {
    this.finishSession();
    this.appInsights?.unload();
    this.appInsights = undefined;
  }

  public trackEvent(event: { name: BotServerAppInsightsEvent } & IEventTelemetry): void {
    console.log('Tracking event:', event);
    if (!this.sessionOpen) {
      console.warn('Session is not open. Cannot track event:', event.name);
      return;
    }

    this.appInsights?.trackEvent({
      ...event,
      properties: {
        operationId: this.operationId,
        ...(event.properties || {})
      }
    });
  }

  public trackDependencyData(dependency: { name: BotServerAppInsightsEvent } & IDependencyTelemetry): void {
    console.log('Tracking event:', dependency);
    if (!this.sessionOpen) {
      console.warn('Session is not open. Cannot track dependency:', dependency.name);
      return;
    }

    this.appInsights?.trackDependencyData({
      ...dependency,
      properties: {
        operationId: this.operationId,
        ...(dependency.properties || {})
      }
    });
  }

  public trackException(exception: IExceptionTelemetry): void {
    console.log('Tracking event:', exception);
    if (!this.sessionOpen) {
      console.warn('Session is not open. Cannot track exception');
      return;
    }

    this.appInsights?.trackException({
      ...exception,
      properties: {
        operationId: this.operationId,
        ...(exception.properties || {})
      }
    });
  }

  public trackEventStart(eventName: BotServerAppInsightsEvent): void {
    console.log('Tracking event start:', eventName);
    if (!this.sessionOpen) {
      console.warn('Session is not open. Cannot track event:', eventName);
      return;
    }

    this.appInsights?.startTrackEvent(eventName);
  }

  public trackEventStop(eventName: BotServerAppInsightsEvent, properties?: object, measurements?: object): void {
    console.log('Tracking event stop:', eventName, properties);
    if (!this.sessionOpen) {
      console.warn('Session is not open. Cannot track event:', eventName);
      return;
    }

    this.appInsights?.stopTrackEvent(
      eventName,
      {
        ...properties,
        operationId: this.operationId
      },
      measurements
    );
  }

  public trackEventSuccess(eventName: BotServerAppInsightsEvent, properties?: object, measurements?: object): void {
    return this.trackEventStop(
      eventName,
      {
        ...properties,
        success: true
      },
      measurements
    );
  }

  public trackEventFail(eventName: BotServerAppInsightsEvent, properties?: object, measurements?: object): void {
    return this.trackEventStop(
      eventName,
      {
        ...properties,
        success: false
      },
      measurements
    );
  }
}

export function callWithEventTracking<T>(
  fn: (...args: unknown[]) => T,
  appInsights: BotServerAppInsights | undefined,
  eventName: BotServerAppInsightsEvent
): T;
export function callWithEventTracking<T>(
  fn: undefined | ((...args: unknown[]) => T),
  appInsights: BotServerAppInsights | undefined,
  eventName: BotServerAppInsightsEvent
): T | undefined;
export function callWithEventTracking<T>(
  fn: undefined | ((...args: unknown[]) => T),
  appInsights: BotServerAppInsights | undefined,
  eventName: BotServerAppInsightsEvent
): T | undefined {
  appInsights?.trackEventStart(eventName);
  try {
    const result = fn?.();
    if (result instanceof Promise) {
      return result
        .then((resolved) => {
          appInsights?.trackEventSuccess(eventName);
          return resolved;
        })
        .catch((error) => {
          appInsights?.trackException({
            exception: error as Error,
            properties: { name: eventName }
          });
          appInsights?.trackEventFail(eventName);
          throw error;
        }) as T;
    } else {
      appInsights?.trackEventSuccess(eventName);
      return result;
    }
  } catch (error) {
    appInsights?.trackException({
      exception: error as Error,
      properties: { name: eventName }
    });
    appInsights?.trackEventFail(eventName);
    throw error;
  }
}
